import type { DocNode } from '@atlaskit/adf-schema';
import { getEmptyADF } from '@atlaskit/adf-utils/empty-adf';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import { parseAdf } from '@atlassian/help-center-common-util/adf';
import type { Field, FieldInternalMetadata, ViewType } from '@atlassian/ui-modifications-core/types';
import { isValidADF } from '@atlassian/ui-modifications-core/utils';

export type TextAreaFieldPublicValue = string | DocNode;

/**
 * Field renderer constants
 */
export const ATLASSIAN_WIKI_RENDERER = 'atlassian-wiki-renderer';

/**
 * Validates that the text area value is appropriate for the field renderer
 */
export const validateTextAreaValue = (
    value: unknown,
    { fieldRenderer }: FieldInternalMetadata,
    _createAnalyticsEvent: CreateUIAnalyticsEvent,
    extra: { currentField: Field; viewType: ViewType }
): Promise<boolean> => {
    if (fieldRenderer === ATLASSIAN_WIKI_RENDERER) {
        return isValidADF({
            newValue: value,
            currentValue: extra.currentField?.value,
        });
    }
    return Promise.resolve(typeof value === 'string');
};

/**
 * Transforms internal text area value to public format
 */
export const textAreaGetValueTransformer = (value: string, fieldRenderer?: string): TextAreaFieldPublicValue => {
    if (value === undefined) {
        return fieldRenderer === ATLASSIAN_WIKI_RENDERER ? getEmptyADF() : '';
    }

    try {
        return parseAdf(value);
    } catch (e) {
        return value;
    }
};

/**
 * Transforms public text area value to internal format
 */
export const textAreaSetValueTransformer = (value: TextAreaFieldPublicValue): string => {
    // Check if value is string, if not convert it to string
    if (typeof value === 'string') {
        return value;
    }
    return JSON.stringify(value);
};
